import React from "react"
// oxlint-disable-next-line no-restricted-imports
import { FloatingPortal as _FloatingPortal } from "@floating-ui/react"

const FloatingPortalRootContext = React.createContext<
  HTMLElement | null | "no-provider"
>("no-provider")

/**
 * Provides a new floating portal root element at the end of the containing dom node.
 *
 * Useful for "toplevel" elements like <dialog>.
 */
export const FloatingPortalRoot = (props: {
  className?: string
  testid?: string
  children?: React.ReactNode
}) => {
  const [root, setRoot] = React.useState<HTMLDivElement | null>(null)

  return (
    <FloatingPortalRootContext.Provider value={root}>
      {props.children}
      <div
        className={props.className}
        ref={setRoot}
        data-testid={props.testid}
      />
    </FloatingPortalRootContext.Provider>
  )
}

export const useFloatingPortalRoot = () => {
  return React.useContext(FloatingPortalRootContext)
}

/**
 * FloatingPortal with default roots configured.
 */
export const FloatingPortal = (
  props: React.ComponentProps<typeof _FloatingPortal>
) => {
  const contextRoot = React.useContext(FloatingPortalRootContext)

  // NOTE: floating ui's FloatingPortal component can't handle switching between using the root and id props
  // after the first render very well. It sometimes ends up rendering nothing in that case.
  let rootProps:
    | { id: string; root: undefined }
    | { root: typeof props.root; id: undefined }

  if (props.id) {
    rootProps = { id: props.id, root: undefined }
  } else if (props.root) {
    rootProps = { root: props.root, id: undefined }
  } else if (contextRoot !== "no-provider") {
    rootProps = { root: contextRoot, id: undefined }
  } else {
    // #modal-root element is defined in the index.html.ejs template (webpack generates html from it)
    rootProps = { id: "modal-root", root: undefined }
  }

  return <_FloatingPortal {...props} {...rootProps} />
}
